import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import useStyles, { TONS, SELOS } from './styles';
import { ATALHOS } from './modulos';
import { pode } from '../../services/acesso';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
const data = (v) => (v ? dayjs(v).format('DD/MM') : '—');

function Vazio({ children }) {
    const classes = useStyles();
    return <div className={classes.vazio}>{children}</div>;
}

export function Kpi({ dados, emDestaque }) {
    const classes = useStyles();
    // No card em destaque (fundo verde) o texto fica branco; a variação vira um selo
    if (emDestaque) {
        return (
            <div>
                <div className={classes.kpiValor} style={{ fontSize: 40 }}>{dados.valor}</div>
                <div className={classes.kpiRodape} style={{ color: '#E4EFDF' }}>
                    <span className={classes.seloDestaque}>{dados.destaque}</span> {dados.rodape}
                </div>
            </div>
        );
    }
    return (
        <div>
            <div className={classes.kpiValor}>{dados.valor}</div>
            <div className={classes.kpiRodape}>
                <span style={{ color: TONS[dados.tom] || TONS.neutro, fontWeight: 600 }}>{dados.destaque}</span> {dados.rodape}
            </div>
        </div>
    );
}

const COR_REALIZADA = '#2F6420';
const COR_AGENDADA = '#B9D3AC';

function Legenda() {
    const classes = useStyles();
    return (
        <div className={classes.legenda}>
            <span><i style={{ background: COR_REALIZADA }} />Realizadas</span>
            <span><i style={{ background: COR_AGENDADA }} />Agendadas</span>
        </div>
    );
}

/** Realizadas (barra cheia, em destaque) sobre agendadas (barra clara), por assessor ou projeto. */
export function Comparativo({ linhas }) {
    const classes = useStyles();
    if (!linhas.length) return <Vazio>Sem reuniões no período.</Vazio>;
    const max = Math.max(...linhas.map((l) => Math.max(l.agendadas, l.realizadas)), 1);
    const w = (n) => `${n > 0 ? Math.max(2, (n / max) * 100) : 0}%`;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Legenda />
            {linhas.map((l) => (
                <div key={l.id} className={classes.compLinha}>
                    <div className={classes.barraRotulo} title={l.nome}>{l.nome}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div className={classes.barraTrilho} style={{ height: 14 }}>
                            <div style={{ height: '100%', width: w(l.realizadas), background: COR_REALIZADA, borderRadius: 5 }} />
                        </div>
                        <div className={classes.barraTrilho} style={{ height: 6 }}>
                            <div style={{ height: '100%', width: w(l.agendadas), background: COR_AGENDADA, borderRadius: 5 }} />
                        </div>
                    </div>
                    <div className={classes.compValores}>
                        <b style={{ color: COR_REALIZADA, fontSize: 15 }}>{fmt(l.realizadas)}</b>
                        <span> / {fmt(l.agendadas)}</span>
                    </div>
                </div>
            ))}
        </div>
    );
}

/** Colunas por dia (ou semana): realizadas na frente, agendadas atrás. */
export function Evolucao({ dados }) {
    const classes = useStyles();
    const serie = (dados && dados.serie) || [];
    if (!serie.length) return <Vazio>Sem dados no período.</Vazio>;
    const semanal = dados.granularidade === 'semana';
    const max = Math.max(...serie.map((d) => Math.max(d.agendadas, d.realizadas)), 1);
    const totalR = serie.reduce((t, d) => t + d.realizadas, 0);
    const totalA = serie.reduce((t, d) => t + d.agendadas, 0);
    const passo = Math.ceil(serie.length / 10); // no máximo ~10 rótulos no eixo
    const rotulo = (d) => (semanal ? 'sem. ' : '') + dayjs(d.data).format('DD/MM');
    const h = (n) => `${n > 0 ? Math.max(3, (n / max) * 100) : 0}%`;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'baseline', justifyContent: 'space-between' }}>
                <Legenda />
                <div className={classes.kpiRodape}>
                    <b style={{ color: COR_REALIZADA }}>{fmt(totalR)}</b> realizadas · {fmt(totalA)} agendadas{semanal ? ' · por semana' : ''}
                </div>
            </div>
            <div className={classes.evolucao} role="img" aria-label={`${totalR} reuniões realizadas e ${totalA} agendadas no período`}>
                {serie.map((d, i) => (
                    <div key={d.data} className={classes.evoColuna} title={`${rotulo(d)}: ${d.realizadas} realizada(s), ${d.agendadas} agendada(s)`}>
                        <div className={classes.evoBarras}>
                            <div style={{ width: '45%', height: h(d.agendadas), background: COR_AGENDADA, borderRadius: '3px 3px 0 0' }} />
                            <div style={{ width: '55%', height: h(d.realizadas), background: COR_REALIZADA, borderRadius: '3px 3px 0 0' }} />
                        </div>
                        <div className={classes.evoRotulo}>{i % passo === 0 ? rotulo(d) : ''}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function Barras({ itens }) {
    const classes = useStyles();
    if (!itens.length) return <Vazio>Sem dados no período.</Vazio>;
    const max = Math.max(...itens.map((i) => i.total), 1);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {itens.map((i, idx) => (
                <div key={idx} className={classes.barraLinha}>
                    <div className={classes.barraRotulo} title={i.rotulo}>{i.rotulo}</div>
                    <div className={classes.barraTrilho}>
                        <div style={{ height: '100%', width: `${Math.max(i.total > 0 ? 2 : 0, (i.total / max) * 100)}%`, background: i.cor, borderRadius: 5 }} />
                    </div>
                    <div className={classes.barraValor}>{fmt(i.total)}</div>
                </div>
            ))}
        </div>
    );
}

function Selo({ tom, children }) {
    const classes = useStyles();
    return <span className={classes.selo} style={SELOS[tom] || SELOS.neutro}>{children}</span>;
}

const TOM_STATUS_REUNIAO = {
    confirmado: 'info', aguardando: 'alerta', reagendada: 'alerta', aguardando_nova_data: 'neutro',
};

export function ProximasReunioes({ linhas }) {
    const classes = useStyles();
    if (!linhas.length) return <Vazio>Nenhuma reunião a partir de hoje.</Vazio>;
    return (
        <div className={classes.tabelaWrap}>
            <table className={classes.tabela}>
                <thead>
                    <tr>
                        <th>Data · hora</th>
                        <th>Razão social</th>
                        <th>Projeto</th>
                        <th>Formato</th>
                        <th>Briefing</th>
                        <th style={{ textAlign: 'right' }}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {linhas.map((r) => (
                        <tr key={r.id}>
                            <td style={{ whiteSpace: 'nowrap' }}>{data(r.data_reuniao)}{r.hora_reuniao ? ` · ${r.hora_reuniao}` : ''}</td>
                            <td style={{ fontWeight: 600 }}>
                                <Link to={`/agendamento-lead/${r.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>{r.razao_social || `Reunião #${r.numero_reuniao}`}</Link>
                            </td>
                            <td>{r.cliente || '—'}</td>
                            <td>{r.formato_rotulo || '—'}</td>
                            <td style={{ color: r.enviou_briefing ? undefined : '#8E2F12' }}>{r.enviou_briefing ? 'Enviado' : 'Pendente'}</td>
                            <td style={{ textAlign: 'right' }}><Selo tom={TOM_STATUS_REUNIAO[r.status] || 'neutro'}>{r.status_rotulo}</Selo></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function FollowUps({ linhas }) {
    const classes = useStyles();
    if (!linhas.length) return <Vazio>Nenhum follow-up vencido ou para hoje.</Vazio>;
    return (
        <div>
            {linhas.map((r) => (
                <Link key={r.mailing_leads_id} to={`/leads/${r.lead_id}/edit?mailing_leads_id=${r.mailing_leads_id}`} className={classes.linhaLista}>
                    <div className={classes.listaLead}>{data(r.follow_up)}</div>
                    <div style={{ minWidth: 0 }}>
                        <div className={classes.listaTitulo}>{r.razao_social}</div>
                        <div className={classes.listaSub}>{r.mailing} · {r.status_rotulo}{r.assessor ? ` · ${r.assessor}` : ''}</div>
                    </div>
                    <Selo tom={r.atrasado ? 'erro' : 'alerta'}>{r.atrasado ? 'Atrasado' : 'Hoje'}</Selo>
                </Link>
            ))}
        </div>
    );
}

export function Auditoria({ linhas }) {
    const classes = useStyles();
    if (!linhas.length) return <Vazio>Nenhuma reunião aguardando auditoria.</Vazio>;
    return (
        <div>
            {linhas.map((r) => (
                <Link key={r.id} to={`/agendamento-lead/${r.id}`} className={classes.linhaLista}>
                    <div className={classes.listaLead}>#{r.numero_reuniao}</div>
                    <div style={{ minWidth: 0 }}>
                        <div className={classes.listaTitulo}>{r.razao_social || '—'}</div>
                        <div className={classes.listaSub}>{r.cliente || '—'} · {r.assessor || '—'} · agendada {dayjs(r.created_at).format('DD/MM')}</div>
                    </div>
                    <Selo tom="alerta">Aguardando</Selo>
                </Link>
            ))}
        </div>
    );
}

const TOM_IMPORTACAO = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('erro') || s.includes('falh')) return 'erro';
    if (s.includes('conclu') || s.includes('finaliz') || s.includes('sucesso')) return 'ok';
    return 'info';
};

export function Importacoes({ linhas }) {
    const classes = useStyles();
    if (!linhas.length) return <Vazio>Nenhuma importação de leads.</Vazio>;
    return (
        <div>
            {linhas.map((r) => (
                <div key={r.id} className={classes.linhaLista}>
                    <div className={classes.listaLead}>{data(r.datahora_importacoes || r.created_at)}</div>
                    <div style={{ minWidth: 0 }}>
                        <div className={classes.listaTitulo} title={r.arquivo}>{r.arquivo}</div>
                        <div className={classes.listaSub}>{Number(r.erros) > 0 ? `${fmt(r.erros)} linha(s) com erro` : 'Sem erros registrados'}</div>
                    </div>
                    <Selo tom={TOM_IMPORTACAO(r.status)}>{r.status}</Selo>
                </div>
            ))}
        </div>
    );
}

export function Atalhos({ visao }) {
    const classes = useStyles();
    const itens = ATALHOS.filter((a) => (visao === 'gestao' || !a.somenteGestao) && pode(a.acao));
    if (!itens.length) return <Vazio>Nenhuma ação disponível para o seu perfil.</Vazio>;
    return (
        <div className={classes.atalhos}>
            {itens.map((a) => (
                <Link key={a.to} to={a.to} className={classes.atalho}>
                    <span aria-hidden="true" style={{ color: '#2F6420', fontWeight: 700 }}>+</span>{a.rotulo}
                </Link>
            ))}
        </div>
    );
}
